import React, { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import ConfirmModal from "@/dashboard/core/components/ConfirmModal";
import SidebarCompanyLogo from "@/dashboard/shared/components/Sidebar/SidebarCompanyLogo";
import SidebarNavItem from "@/dashboard/shared/components/Sidebar/SidebarNavItem";
import { connect } from "react-redux";
import { contentPagesApi } from "@/api/contentPagesApi";
import iconAiUnfilled from "@/images/ic_ai_unfilled.png";
import iconEmail from "@/images/ic_email.png";
import iconGroupUnfilled from "@/images/ic_group_unfilled.png";
import iconHomeUnfilled from "@/images/ic_home_unfilled.png";
import iconLogout from "@/images/ic_logout.png";
import iconMedia from "@/images/ic_media.png";
import iconPage from "@/images/ic_page.png";
import iconSankey from "@/images/ic_sankey.png";
import iconSettings from "@/images/ic_settings.png";
import { jobClipsApi } from "@/api/jobClipsApi";
import log from "loglevel";
import logo from "@/images/puck-logo.png";
import { safePrefetch } from "@/utils/prefetchUtils";
import styled from "styled-components";
import theme from "@/styles/theme";
import { useDispatch } from "react-redux";

const Container = styled.div`
  position: sticky;
  top: 0;
  left: 0;
  width: 216px;
  height: 100vh;
  display: flex;
  flex-direction: column;
`;

const LogoContainer = styled.div`
  display: flex;
  align-items: center;
  height: 64px;
  padding-left: ${theme.spacing.unit * 2}px;
  box-sizing: border-box;
  flex-shrink: 0;
`;

const OptionsContainer = styled.div`
  padding: ${theme.spacing.unit * 1}px ${theme.spacing.unit * 2}px;
  display: flex;
  flex-direction: column;
  row-gap: 4px;
`;

// Individual functions for each sidebar item - focused on when they SHOULD be visible
const addHomeOption = (options, config, pageStates) => {
  options.push({
    title: "Home",
    icon: iconHomeUnfilled,
    path: "/employers/home",
    isSelected: pageStates.isHomePage,
  });
};

const addTalentOption = (options, config, pageStates) => {
  if (config.isGuest) {
    return;
  }

  // Either they're not free tier, or they are free tier but have Puck ATS
  if (config.crmEnabled && (!config.isSelfServe || config.hasPuckAts)) {
    options.push({
      title: "Talent",
      icon: iconGroupUnfilled,
      path: "/employers/talent-search",
      isHighlighted: pageStates.isTalentPage,
    });
  }
};

const addAllTalentOption = (options, config, pageStates) => {
  if (pageStates.isTalentPage) {
    options.push({
      title: "Search",
      path: "/employers/talent-search",
      isSelected: pageStates.isTalentSearchPage,
    });
  }
};

const addTalentListsOption = (options, config, pageStates) => {
  if (pageStates.isTalentPage) {
    options.push({
      title: "Lists",
      path: "/employers/talent-lists",
      isSelected: pageStates.isTalentListsPage,
    });
  }
};

const addSequencesOption = (options, config, pageStates) => {
  if (config.isGuest) {
    return;
  }

  // Either they're not free tier, or they are free tier but have Puck ATS
  if (config.crmEnabled && (!config.isSelfServe || config.hasPuckAts)) {
    options.push({
      title: "Sequences",
      icon: iconEmail,
      path: "/employers/sequence-campaigns",
      isHighlighted: pageStates.isSequencesPage,
    });
  }
};

const addSequenceCampaignsOption = (options, config, pageStates) => {
  if (config.isGuest) {
    return;
  }

  if (pageStates.isSequencesPage) {
    options.push({
      title: "Campaigns",
      path: "/employers/sequence-campaigns",
      isSelected: pageStates.isSequenceCampaignsPage,
    });
  }
};

const addSequenceNotificationsOption = (options, config, pageStates) => {
  if (config.isGuest) {
    return;
  }

  if (pageStates.isSequencesPage) {
    options.push({
      title: "Notifications",
      path: "/employers/sequence-notifications",
      isSelected: pageStates.isSequenceNotificationsPage,
    });
  }
};

const addApplicantMatchOption = (options, config, pageStates) => {
  if (config.isGuest) {
    return;
  }

  if (config.hasAtsSync) {
    options.push({
      title: "Applicant Match",
      icon: iconAiUnfilled,
      path: "/employers/applicant-match",
      isSelected: pageStates.isApplicantMatchPage,
    });
  }
};

const addPipelinesOption = (options, config, pageStates) => {
  if (config.isGuest) {
    return;
  }

  if (config.hasPuckAts) {
    options.push({
      title: "Pipelines",
      icon: iconSankey,
      path: "/employers/pipelines",
      isSelected: pageStates.isPipelinesPage,
    });
  }
};

const addPagesOption = (options, config, pageStates, prefetchPages) => {
  if (config.isGuest) {
    return;
  }

  // If they have Puck ATS and are free tier, they can see job pages only
  if (config.hasPuckAts && config.isSelfServe) {
    options.push({
      title: "Pages",
      icon: iconPage,
      path: "/employers/job-pages",
    });

    return;
  }

  // If they aren't self-serve, they can see all pages
  if (!config.isSelfServe) {
    options.push({
      title: "Pages",
      icon: iconPage,
      path: "/employers/interview-guides",
      isHighlighted: pageStates.isPagesPage,
      onMouseEnter: () => {
        log.debug("Pages hover detected, prefetching pages data");
        prefetchPages();
      },
    });
  }
};

const addInterviewGuidesOption = (options, config, pageStates) => {
  if (config.isGuest) {
    return;
  }

  // If they're self-serve, they can't see interview guides
  if (config.isSelfServe) {
    return;
  }

  if (pageStates.isPagesPage) {
    options.push({
      title: "Interview Guides",
      path: "/employers/interview-guides",
      isSelected: pageStates.isInterviewGuidesPage,
    });
  }
};

const addJobPagesOption = (options, config, pageStates) => {
  if (config.isGuest) {
    return;
  }

  if (pageStates.isPagesPage && config.hasJobBoard) {
    options.push({
      title: "Job Pages",
      path: "/employers/job-pages",
      isSelected: pageStates.isJobPagesPage,
    });
  }
};

const addCampaignPagesOption = (options, config, pageStates) => {
  if (config.isGuest) {
    return;
  }

  // If they're self-serve, they can't see campaign pages
  if (config.isSelfServe) {
    return;
  }

  if (pageStates.isPagesPage) {
    options.push({
      title: "Campaign Pages",
      path: "/employers/campaign-pages",
      isSelected: pageStates.isCampaignPagesPage,
    });
  }
};

const addContentLibraryOption = (
  options,
  config,
  pageStates,
  location,
  prefetchContentLibrary
) => {
  options.push({
    title: "Content Library",
    icon: iconMedia,
    path: "/employers/content-library",
    isSelected: location.pathname.startsWith("/employers/content-library"),
    onMouseEnter: () => {
      log.debug("Content Library hover detected, prefetching job clips");
      prefetchContentLibrary();
    },
  });
};

const addSettingsOption = (options, config, pageStates, location) => {
  // Settings is available to all users
  options.push({
    title: "Settings",
    icon: iconSettings,
    path: "/employers/settings/profile",
    isSelected: location.pathname.startsWith("/employers/settings"),
  });
};

const addLogoutOption = (options, config, pageStates, handleLogoutClick) => {
  // Logout is available to all users
  options.push({
    title: "Log out",
    icon: iconLogout,
    onClick: handleLogoutClick,
    isSelected: false,
  });
};

// Main function to build sidebar options
const buildSidebarOptions = (
  user,
  pageStates,
  handleLogoutClick,
  location,
  prefetchPages,
  prefetchContentLibrary
) => {
  const config = {
    crmEnabled: user.employerProfile.crmEnabled,
    hasAtsSync: user.employerProfile.hasAtsSync,
    hasPuckAts: user.employerProfile.hasPuckAts,
    hasJobBoard: user?.employerProfile?.jobBoards?.length > 0,
    isSelfServe: user?.employerProfile?.subscriptionTier === "free",
    isGuest: user.role === "guest",
  };

  const options = [];

  // Add each sidebar item using individual functions
  addHomeOption(options, config, pageStates);
  addTalentOption(options, config, pageStates);
  addAllTalentOption(options, config, pageStates);
  addTalentListsOption(options, config, pageStates);
  addSequencesOption(options, config, pageStates);
  addSequenceCampaignsOption(options, config, pageStates);
  addSequenceNotificationsOption(options, config, pageStates);
  addApplicantMatchOption(options, config, pageStates);
  addPipelinesOption(options, config, pageStates);
  addPagesOption(options, config, pageStates, prefetchPages);
  addInterviewGuidesOption(options, config, pageStates);
  addJobPagesOption(options, config, pageStates);
  addCampaignPagesOption(options, config, pageStates);
  addContentLibraryOption(
    options,
    config,
    pageStates,
    location,
    prefetchContentLibrary
  );
  addSettingsOption(options, config, pageStates, location);
  addLogoutOption(options, config, pageStates, handleLogoutClick);

  return options;
};

const Sidebar = ({ user }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const [isLogoutConfirmModalVisible, setIsLogoutConfirmModalVisible] =
    useState(false);

  // Create prefetch functions using dispatch without subscription
  const prefetchPages = () => {
    if (user?.employerId) {
      const interviewGuidesParams = {
        employerPermalink: user.employerId,
        perPage: 50,
        cursor: null, // matches usePagination default
        searchQuery: "", // matches useSearch default (empty string, not null)
        types: ["interview_guide"],
      };
      safePrefetch(
        dispatch,
        contentPagesApi.endpoints.getContentPages.initiate,
        { ...interviewGuidesParams }
      );
    }
  };

  const prefetchContentLibrary = () => {
    if (user?.employerId) {
      const jobClipsParams = {
        employerPermalink: user.employerId,
        perPage: 50,
        cursor: null, // matches usePagination default
        searchQuery: "", // matches useSearch default (empty string, not null)
      };
      safePrefetch(dispatch, jobClipsApi.endpoints.getJobClips.initiate, {
        ...jobClipsParams,
      });
    }
  };

  const handleLogoutClick = () => {
    setIsLogoutConfirmModalVisible(true);
  };

  const handleLogoutCancel = () => {
    setIsLogoutConfirmModalVisible(false);
  };

  const handleLogoutConfirm = () => {
    setIsLogoutConfirmModalVisible(false);
    navigate(`/logout`);
  };

  // Page state calculations
  const isHomePage = location.pathname.startsWith("/employers/home");

  const isTalentSearchPage = location.pathname.startsWith(
    "/employers/talent-search"
  );
  const isTalentListsPage =
    location.pathname.startsWith("/employers/talent-lists") ||
    location.pathname.startsWith("/employers/talent-list/");
  const isTalentPage = isTalentSearchPage || isTalentListsPage;

  const isSequenceCampaignsPage =
    location.pathname.startsWith("/employers/sequence-campaigns") ||
    location.pathname.startsWith("/employers/sequence-campaign/");
  const isSequenceNotificationsPage =
    location.pathname.startsWith("/employers/sequence-notifications") ||
    location.pathname.startsWith("/employers/sequence-notification/");

  const isInterviewGuidesPage =
    location.pathname.startsWith("/employers/interview-guides") ||
    location.pathname.startsWith("/employers/interview-guide/");
  const isJobPagesPage =
    location.pathname.startsWith("/employers/job-pages") ||
    location.pathname.startsWith("/employers/job-page/");
  const isApplicantMatchPage = location.pathname.startsWith(
    "/employers/applicant-match"
  );
  const isCampaignPagesPage =
    location.pathname.startsWith("/employers/campaign-pages") ||
    location.pathname.startsWith("/employers/campaign-page/");

  const isPipelinesPage =
    location.pathname.startsWith("/employers/pipelines") ||
    location.pathname.startsWith("/employers/pipeline/");

  const isSequencesPage =
    isSequenceCampaignsPage || isSequenceNotificationsPage;
  const isPagesPage =
    isInterviewGuidesPage || isJobPagesPage || isCampaignPagesPage;

  const pageStates = {
    isHomePage,
    isTalentSearchPage,
    isTalentListsPage,
    isTalentPage,
    isSequenceCampaignsPage,
    isSequenceNotificationsPage,
    isInterviewGuidesPage,
    isJobPagesPage,
    isApplicantMatchPage,
    isCampaignPagesPage,
    isPipelinesPage,
    isSequencesPage,
    isPagesPage,
  };

  if (!user) {
    return null;
  }

  const options = buildSidebarOptions(
    user,
    pageStates,
    handleLogoutClick,
    location,
    prefetchPages,
    prefetchContentLibrary
  );

  return (
    <>
      <ConfirmModal
        title={"Are you sure you want to log out?"}
        isVisible={isLogoutConfirmModalVisible}
        cancelButtonLabel={"No"}
        onCancel={handleLogoutCancel}
        confirmButtonLabel={"Yes"}
        onConfirm={handleLogoutConfirm}
      />
      <Container>
        <LogoContainer>
          <a href="https://www.careerpuck.com">
            <img src={logo} width={70} height={24.25} alt="" />
          </a>
        </LogoContainer>
        <OptionsContainer data-testing={"sidebar"}>
          {options.map((option) => {
            if (option.isHidden) {
              return null;
            }
            return <SidebarNavItem key={option.title} {...option} />;
          })}
        </OptionsContainer>
        <SidebarCompanyLogo user={user} />
      </Container>
    </>
  );
};

function mapStateToProps(state) {
  const { auth } = state;
  return {
    user: auth.user,
  };
}

export default connect(mapStateToProps)(Sidebar);
